import { useMutation } from '@apollo/client';
import React, { FC, useCallback, useMemo, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import { ApprovalStatus, ApprovalType, Task, UpdateStatus } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import { EntitlementPageLink, SlackChannelLink } from '../common';
import { ModalTitle } from '../constants/modal-titles';
import { updateTask as UPDATE_TASK } from '../queries/tasks';
import ErrorNotificationMessage from './error-notification-message';
import './modal.scss';
import { useModalNotification } from './use-modal-notification';

interface RequestModalProps {
  task: Task;
  onComplete?: () => void;
  closeModal: () => void;
}

const ReplyModal: FC<RequestModalProps> = ({ task, onComplete, closeModal }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [updateTask] = useMutation<{ updateTask: { status: UpdateStatus } }>(UPDATE_TASK);

  const userProfile = useApplicationContext();

  const [renderSuccessNotification, renderErrorNotification] = useModalNotification(
    closeModal,
    'reply-modal-notification'
  );

  const entitlementPageLink = useMemo(() => {
    if (task.businessKey && task.entityType) {
      return (
        <EntitlementPageLink entityNumber={task.businessKey} entityType={task.entityType} entityId={task.entityId} />
      );
    }
    return '';
  }, [task]);

  const notification = useMemo(() => {
    if (task.requestType) {
      switch (task.requestType) {
        case ApprovalType.QUOTE_EXTENSION:
          return {
            title: 'Reasons submitted',
            message: (
              <p>
                You’ve provided supporting reasons for the request {task.taskNumber} for the quote {entitlementPageLink}
              </p>
            ),
          };
        default:
          return {
            title: 'Comment submitted',
            message: `You can view your comment for request ${task.taskNumber} in Approval Desk.`,
          };
      }
    }
    return { title: '', message: '' };
  }, [task]);

  const handleFormSubmit = useCallback(
    async ({ comment }: { comment: string }) => {
      setLoading(true);
      const userID = userProfile?.userId?.split('@')?.[0];
      try {
        const result = await updateTask({
          variables: {
            taskId: task.taskId,
            approvalStatus: ApprovalStatus.REASONS_PROVIDED,
            id: userID,
            comment,
          },
        });
        const errorMsg = result?.errors?.[0]?.extensions?.message;
        if (errorMsg) {
          throw new Error(errorMsg);
        }
        const status = result?.data?.updateTask?.status ?? '';
        if (status === 'OK') {
          onComplete?.();
          renderSuccessNotification(notification.title, notification.message);
        }
      } catch (e: any) {
        renderErrorNotification(
          'We’re unable to submit your comment',
          <ErrorNotificationMessage errorMessage={e.message} />
        );
      } finally {
        setLoading(false);
      }
    },
    [task.taskId]
  );

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <div className="modal-form-wrapper" data-testid="reply-modal">
          <form {...formProps}>
            <FormHeader
              title={
                <div className="modal-title-wrapper">
                  <span className="modal-title">{ModalTitle.REPLY}</span>
                </div>
              }
            />
            <p>
              Your response will be added to the request in the Slack channel{' '}
              <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
            </p>
            <FormSection>
              <Field<string, HTMLTextAreaElement>
                name="comment"
                label="Internal comment"
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <TextArea
                      {...fieldProps}
                      placeholder="Add comment"
                      testId="comment-input"
                      isRequired={true}
                      isInvalid={!!error}
                    />
                    {error && <ErrorMessage testId="comment-input-error">This field is required</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeModal} testId="cancel-btn">
                  Cancel
                </Button>
                <LoadingButton appearance="primary" autoFocus type="submit" isLoading={loading} testId="submit-btn">
                  Submit
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        </div>
      )}
    </Form>
  );
};

export default ReplyModal;
